import type { ReportValue } from "../../../http/deckDesigner/types/report";
import type { MeasurementFormat, MetricLengthUnit } from "./measurementFormat";
import { EMPTY_VALUE, isFiniteNumber } from "../../../utils/numberFormat";

// Language-neutral values for the PDF report, which the server formats in the report's language
// (see ReportValue). Imperial measurements read the same in every language, so they go as text.
export const createReportValues = (measurementFormat: MeasurementFormat) => {
  const isImperial = measurementFormat.isImperial;
  const numberValue = (value: number | null | undefined, unit?: string, decimals = 0): ReportValue | null =>
    isFiniteNumber(value) ? { number: value, decimals, unit } : null;

  return {
    isImperial,
    numberValue,
    piecesValue: (value: number | null | undefined): ReportValue | null => numberValue(value, "pcs"),
    lengthValue: (value: number | null | undefined, unit: MetricLengthUnit, decimals = 0): ReportValue | null =>
      isFiniteNumber(value)
        ? isImperial
          ? { text: measurementFormat.length(value, unit, "") }
          : { number: value, decimals, unit }
        : null,
    // Whole millimeters shown as they are, without grouping - the same text in every language.
    mmValue: (value: number): ReportValue => ({ text: measurementFormat.length(value, "mm", `${value} mm`) }),
    liveLoadValue: (kilonewtonsPerSquareMeter: number): ReportValue =>
      isImperial
        ? { text: measurementFormat.liveLoad(kilonewtonsPerSquareMeter, "") }
        : { number: kilonewtonsPerSquareMeter, unit: "kN/m²" },
    reactionValue: (kilonewtons: number): ReportValue =>
      isImperial
        ? { text: measurementFormat.force(kilonewtons, "") }
        : { number: kilonewtons, decimals: 2, unit: "kN" },
    // A row the report shows even without a value, as "-".
    orEmpty: (value: ReportValue | null): ReportValue => value ?? { text: EMPTY_VALUE },
  };
};

export type ReportValues = ReturnType<typeof createReportValues>;
